import React, { useEffect, useState, useCallback } from "react";
import { Users, Briefcase, Megaphone, Coins, KeyRound, Flame, Plus, Shield, Zap, AlertTriangle, Ban, RefreshCcw } from "lucide-react";
import Header from "../components/Header";
import { api } from "../lib/api";
import { useTheme } from "../context/ThemeContext";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";

const MARS_ORANGE = "#FF4500";

export default function AdminCommander() {
  const { theme } = useTheme();
  const [tab, setTab] = useState("clients");
  const [clients, setClients] = useState([]);
  const [investors, setInvestors] = useState([]);
  const [affiliates, setAffiliates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, i, a] = await Promise.all([
        api.get("/commander/clients"),
        api.get("/commander/investors"),
        api.get("/commander/affiliates"),
      ]);
      setClients(c.data.items || []);
      setInvestors(i.data.items || []);
      setAffiliates(a.data.items || []);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Client actions
  const overrideCreditRate = async (id, rate) => {
    try {
      await api.patch(`/commander/clients/${id}/credit-rate`, { credit_rate: rate });
      toast.success(`Credit rate set to ${rate}×`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };
  const rotateApiKey = async (id) => {
    try {
      const r = await api.post(`/commander/clients/${id}/rotate-api-key`, {});
      toast.success("API key rotated");
      navigator.clipboard?.writeText(r.data.api_key);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  // Investor actions
  const adjustBlocks = async (id, delta) => {
    const reason = window.prompt(`${delta > 0 ? "Credit" : "Burn"} ${Math.abs(delta)} blocks. Reason?`, "manual adjust");
    if (reason === null) return;
    try {
      await api.post(`/commander/investors/${id}/blocks`, { delta, reason });
      toast.success(`${delta > 0 ? "Credited" : "Burned"} ${Math.abs(delta)} blocks`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  // Affiliate actions
  const affiliateAction = async (action, id, label) => {
    const reason = window.prompt(`${label}. Reason?`, `admin action: ${label}`);
    if (reason === null) return;
    try {
      await api.post(`/commander/affiliates/${id}/${action}`, { reason });
      toast.success(`${label} executed`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  return (
    <div className="min-h-screen bg-[#030303] text-white">
      <Header />
      <main className="pt-24 pb-16 max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-2">
          <Shield className="h-3 w-3 theme-text" /> Master commander · Absolute CRUD
        </div>
        <h1 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight">
          <span style={{ color: theme.color }}>Commander</span> Console
        </h1>
        <p className="text-zinc-400 mt-2">Clients, Investors, Affiliates — full override.</p>

        <div className="mt-8 grid md:grid-cols-3 gap-4" data-testid="commander-stats">
          <StatCard icon={<Briefcase className="h-4 w-4" />} label="B2B Clients" value={clients.length} />
          <StatCard icon={<Users className="h-4 w-4" />} label="Retail Investors" value={investors.length} />
          <StatCard icon={<Megaphone className="h-4 w-4" />} label="Affiliates" value={affiliates.length} />
        </div>

        <Tabs value={tab} onValueChange={setTab} className="mt-10">
          <TabsList className="bg-black/40 border border-white/10" data-testid="commander-tabs">
            <TabsTrigger value="clients" data-testid="tab-clients" className="data-[state=active]:bg-[var(--theme-primary)] data-[state=active]:text-black">
              <Briefcase className="h-3.5 w-3.5 mr-1.5" /> Clients
            </TabsTrigger>
            <TabsTrigger value="investors" data-testid="tab-investors" className="data-[state=active]:bg-[var(--theme-primary)] data-[state=active]:text-black">
              <Users className="h-3.5 w-3.5 mr-1.5" /> Investors
            </TabsTrigger>
            <TabsTrigger value="affiliates" data-testid="tab-affiliates" className="data-[state=active]:bg-[var(--theme-primary)] data-[state=active]:text-black">
              <Megaphone className="h-3.5 w-3.5 mr-1.5" /> Affiliates
            </TabsTrigger>
          </TabsList>

          {/* CLIENTS */}
          <TabsContent value="clients" className="mt-5">
            <div className="border border-white/10 bg-[#0A0A0A]" data-testid="commander-clients-table">
              {error ? <p className="p-6 text-sm text-red-400">{error}</p>
                : loading ? <p className="p-6 text-sm text-zinc-400">Loading…</p>
                : clients.length === 0 ? <p className="p-6 text-sm text-zinc-400">No B2B clients yet.</p>
                : (
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/10">
                      <TableHead className="text-[11px] uppercase tracking-[0.2em] text-zinc-500">Name</TableHead>
                      <TableHead className="text-[11px] uppercase tracking-[0.2em] text-zinc-500">Email</TableHead>
                      <TableHead className="text-[11px] uppercase tracking-[0.2em] text-zinc-500">API Key</TableHead>
                      <TableHead className="text-[11px] uppercase tracking-[0.2em] text-zinc-500">Credits</TableHead>
                      <TableHead className="text-[11px] uppercase tracking-[0.2em] text-zinc-500">Rate ×</TableHead>
                      <TableHead className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 text-right">Override</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {clients.map((u) => (
                      <TableRow key={u.id} className="border-white/5 hover:bg-white/5" data-testid={`client-row-${u.id}`}>
                        <TableCell className="font-medium text-sm">{u.name || "—"}</TableCell>
                        <TableCell className="text-zinc-300 text-sm">{u.email}</TableCell>
                        <TableCell className="font-mono text-xs text-zinc-400 max-w-[220px] truncate" title={u.api_key || ""}>
                          {u.api_key || <span className="text-zinc-600">— no key —</span>}
                        </TableCell>
                        <TableCell className="text-zinc-300 font-mono text-sm">
                          <span className="inline-flex items-center gap-1"><Coins className="h-3 w-3 theme-text" /> {u.credits}</span>
                        </TableCell>
                        <TableCell className="font-mono text-sm" style={{ color: u.credit_rate > 1 ? theme.color : u.credit_rate < 1 ? MARS_ORANGE : "#a1a1aa" }}>
                          {u.credit_rate.toFixed(1)}×
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="inline-flex items-center gap-1">
                            <ActionBtn onClick={() => overrideCreditRate(u.id, 0.5)} testid={`throttle-${u.id}`} title="Throttle (0.5×)" mars>
                              <AlertTriangle className="h-3 w-3" />
                            </ActionBtn>
                            <ActionBtn onClick={() => overrideCreditRate(u.id, 1.0)} testid={`reset-${u.id}`} title="Reset (1×)">
                              <RefreshCcw className="h-3 w-3" />
                            </ActionBtn>
                            <ActionBtn onClick={() => overrideCreditRate(u.id, 2.5)} testid={`boost-${u.id}`} title="Boost (2.5×)" primary>
                              <Zap className="h-3 w-3" />
                            </ActionBtn>
                            <ActionBtn onClick={() => rotateApiKey(u.id)} testid={`rotate-${u.id}`} title="Rotate API key">
                              <KeyRound className="h-3 w-3" />
                            </ActionBtn>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          </TabsContent>

          {/* INVESTORS */}
          <TabsContent value="investors" className="mt-5">
            <div className="border border-white/10 bg-[#0A0A0A]" data-testid="commander-investors-table">
              {loading ? <p className="p-6 text-sm text-zinc-400">Loading…</p>
                : investors.length === 0 ? <p className="p-6 text-sm text-zinc-400">No investors yet.</p>
                : (
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/10">
                      <TableHead className="text-[11px] uppercase tracking-[0.2em] text-zinc-500">Name</TableHead>
                      <TableHead className="text-[11px] uppercase tracking-[0.2em] text-zinc-500">Wallet</TableHead>
                      <TableHead className="text-[11px] uppercase tracking-[0.2em] text-zinc-500">Blocks</TableHead>
                      <TableHead className="text-[11px] uppercase tracking-[0.2em] text-zinc-500">Invites left</TableHead>
                      <TableHead className="text-[11px] uppercase tracking-[0.2em] text-zinc-500">Investor code</TableHead>
                      <TableHead className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 text-right">Override</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {investors.map((u) => (
                      <TableRow key={u.id} className="border-white/5 hover:bg-white/5" data-testid={`investor-row-${u.id}`}>
                        <TableCell className="font-medium text-sm">{u.name || "—"}</TableCell>
                        <TableCell className="font-mono text-xs text-zinc-400 max-w-[180px] truncate" title={u.wallet_address || ""}>
                          {u.wallet_address || <span className="text-zinc-600">— not set —</span>}
                        </TableCell>
                        <TableCell className="font-mono text-sm theme-text tabular-nums">{u.block_balance.toLocaleString()}</TableCell>
                        <TableCell className="font-mono text-sm text-zinc-300 tabular-nums">{u.invite_codes_remaining}</TableCell>
                        <TableCell className="font-mono text-xs">{u.investor_code || "—"}</TableCell>
                        <TableCell className="text-right">
                          <div className="inline-flex items-center gap-1">
                            <ActionBtn onClick={() => adjustBlocks(u.id, 100)} testid={`credit-${u.id}`} title="Credit 100 blocks" primary>
                              <Plus className="h-3 w-3" /> 100
                            </ActionBtn>
                            <ActionBtn onClick={() => adjustBlocks(u.id, -100)} testid={`burn-${u.id}`} title="Burn 100 blocks" mars>
                              <Flame className="h-3 w-3" /> 100
                            </ActionBtn>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          </TabsContent>

          {/* AFFILIATES */}
          <TabsContent value="affiliates" className="mt-5">
            <div className="border border-white/10 bg-[#0A0A0A]" data-testid="commander-affiliates-table">
              {loading ? <p className="p-6 text-sm text-zinc-400">Loading…</p>
                : affiliates.length === 0 ? <p className="p-6 text-sm text-zinc-400">No affiliates yet.</p>
                : (
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/10">
                      <TableHead className="text-[11px] uppercase tracking-[0.2em] text-zinc-500">Name</TableHead>
                      <TableHead className="text-[11px] uppercase tracking-[0.2em] text-zinc-500">Affiliate code</TableHead>
                      <TableHead className="text-[11px] uppercase tracking-[0.2em] text-zinc-500">Week blocks</TableHead>
                      <TableHead className="text-[11px] uppercase tracking-[0.2em] text-zinc-500">Pending $</TableHead>
                      <TableHead className="text-[11px] uppercase tracking-[0.2em] text-zinc-500">SLA</TableHead>
                      <TableHead className="text-[11px] uppercase tracking-[0.2em] text-zinc-500">Status</TableHead>
                      <TableHead className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 text-right">Override</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {affiliates.map((u) => {
                      const under = u.kpi?.trending_below;
                      return (
                        <TableRow key={u.id} className="border-white/5 hover:bg-white/5" data-testid={`affiliate-row-${u.id}`}>
                          <TableCell className="font-medium text-sm">{u.name || "—"}</TableCell>
                          <TableCell className="font-mono text-xs theme-text font-bold">{u.affiliate_code || "—"}</TableCell>
                          <TableCell className="font-mono text-sm">
                            {u.kpi?.blocks_this_week.toLocaleString() ?? 0} / {u.kpi?.weekly_target.toLocaleString() ?? "4,166"}
                          </TableCell>
                          <TableCell className="font-mono text-sm text-zinc-300">${u.kpi?.pending_escrow_usd?.toLocaleString() ?? "0"}</TableCell>
                          <TableCell>
                            <span
                              className="inline-block px-2 py-0.5 text-[10px] uppercase tracking-[0.15em] border font-bold"
                              style={{
                                borderColor: under ? MARS_ORANGE : "rgba(255,255,255,0.15)",
                                color: under ? MARS_ORANGE : "#a1a1aa",
                              }}
                            >
                              {under ? "At risk" : "On pace"}
                            </span>
                          </TableCell>
                          <TableCell>
                            <span
                              className="inline-block px-2 py-0.5 text-[10px] uppercase tracking-[0.15em] border"
                              style={{
                                borderColor: u.affiliate_status === "active" ? theme.color : MARS_ORANGE,
                                color: u.affiliate_status === "active" ? theme.color : MARS_ORANGE,
                              }}
                            >
                              {u.affiliate_status}
                            </span>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="inline-flex items-center gap-1 flex-wrap justify-end">
                              <ActionBtn onClick={() => affiliateAction("force-payout", u.id, "Force execute escrow payout")} testid={`force-payout-${u.id}`} title="Force escrow payout" primary>
                                <Zap className="h-3 w-3" /> Payout
                              </ActionBtn>
                              <ActionBtn onClick={() => affiliateAction("sla-penalty", u.id, "Trigger SLA liquid penalty (10%)")} testid={`sla-penalty-${u.id}`} title="Trigger 10% SLA penalty" mars>
                                <AlertTriangle className="h-3 w-3" /> 10%
                              </ActionBtn>
                              <ActionBtn onClick={() => affiliateAction("revoke", u.id, "Revoke master link")} testid={`revoke-${u.id}`} title="Revoke master link" mars>
                                <Ban className="h-3 w-3" /> Revoke
                              </ActionBtn>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

function StatCard({ icon, label, value }) {
  return (
    <div className="p-5 border border-white/10 bg-[#0A0A0A]">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-3">
        <span className="theme-text">{icon}</span> {label}
      </div>
      <div className="font-display font-black text-3xl">{value}</div>
    </div>
  );
}

function ActionBtn({ onClick, children, testid, title, primary, mars }) {
  const borderColor = mars ? MARS_ORANGE : primary ? "var(--theme-primary)" : "rgba(255,255,255,0.15)";
  const color = mars ? MARS_ORANGE : primary ? "var(--theme-primary)" : "#d4d4d8";
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      title={title}
      className="inline-flex items-center gap-1 h-8 px-2.5 border text-[11px] font-bold tracking-wide hover:bg-white/5 transition-colors"
      style={{ borderColor, color }}
    >
      {children}
    </button>
  );
}
